import React, { useEffect, useState } from "react";
import {
  message, Button, Select, Modal, DatePicker,
  TimePicker, Input, Form, Divider, Space, Tooltip,
} from "antd";
import { CalendarDaysIcon, MagnifyingGlassIcon, PlusIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import dayjs from "dayjs";
import { debounce } from "lodash";
import {
  ActionButton,
  DataTable,
  FilterPills,
  PageCard,
  PageShell,
  PageToolbar,
  RowIconButton,
  StatCard,
  StatusBadge,
} from "../../components/ui";
import { createLead, fetchLeads, firstApiError } from "../../services/leadGuardianService";
import LeadGuardiansModal from "./LeadGuardiansModal";

const { Option } = Select;

function highlight(text, query) {
  if (!text) return "—";
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="rounded bg-indigo-100 px-0.5 text-[#1D2C7B]">{part}</mark>
    ) : (
      part
    )
  );
}

const CallCenter = () => {
  const { t } = useTranslation();
  const statusTones = {
    new: "blue",
    planned: "amber",
    completed: "green",
    canceled: "red",
  };
  const statusKeys = ["new", "planned", "completed", "canceled"];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", phone: "", base: false, birth_at: null });
  const [guardianLeadId, setGuardianLeadId] = useState(null);
  const [patients, setPatients] = useState([]);
  const [activeStatus, setActiveStatus] = useState("all");
  const [searchName, setSearchName] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [searchDate, setSearchDate] = useState(null);
  const [partners, setPartners] = useState([]);
  const [newUserPartnerId, setNewUserPartnerId] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    new: 0,
    planned: 0,
    completed: 0,
    canceled: 0,
  });

  const fetchStatusCounts = async () => {
    try {
      const res = await axiosInstance.get("/call_center/get/status/counts");
      setStatusCounts(res.data.message);
    } catch (error) {
      message.error(t('callCenter.statsError'));
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await axiosInstance.get("/partners");
      setPartners(res.data.data);
    } catch (error) {
      message.error(t('callCenter.partnersError'));
    }
  };

  const fetchPatients = async () => {
    try {
      const res = await axiosInstance.get("/patient/all");
      setPatients(res.data.data);
    } catch (error) {
      message.error(t('callCenter.patientsError'));
    }
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    const fullPhone = `998${raw}`;
    const match = patients.find((p) => p.phone === fullPhone);

    setNewUser((prev) => ({
      ...prev,
      phone: fullPhone,
      base: !!match,
    }));
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.phone) {
      return message.error(t('callCenter.fillNamePhone'));
    }
    try {
      const payload = {
        name: newUser.name,
        phone: newUser.phone,
        base: newUser.base,
      };
      if (newUserPartnerId) payload.partner_id = newUserPartnerId;
      if (newUser.birth_at) payload.birth_at = newUser.birth_at;
      let created;
      try {
        created = await createLead(payload);
      } catch (error) {
        if (newUser.birth_at && error?.response?.status === 400) throw error;
        const response = await axiosInstance.post('/call_center/store/user', {
          name: newUser.name,
          phone: newUser.phone,
          base: newUser.base,
          partner_id: newUserPartnerId,
        });
        created = response.data?.data ?? response.data;
      }
      message.success(t('callCenter.added'));
      setIsAddModalVisible(false);
      setNewUser({ name: "", phone: "", base: false, birth_at: null });
      setNewUserPartnerId(null);
      fetchCallCenterData(pagination.current);
      if (created?.id && created.is_minor) {
        setGuardianLeadId(created.id);
      }
    } catch (error) {
      const fieldMessage = firstApiError(error);
      message.error(fieldMessage || t('callCenter.addError'));
    }
  };

  const fetchCallCenterData = async (page = 1, status = "all", filters = {}) => {
    setLoading(true);
    try {
      const result = await fetchLeads({
        page,
        perPage: pagination.pageSize,
        status,
        name: filters.name,
        phone: filters.phone,
        createdAt: filters.created_at,
      });
      setData(result.items);
      setPagination(result.pagination);
    } catch (error) {
      try {
        let query = `?page=${page}`;
        if (status !== "all") query += `&status=${status}`;
        if (filters.name) query += `&name=${filters.name}`;
        if (filters.phone) query += `&phone=${filters.phone}`;
        if (filters.created_at) query += `&created_at=${filters.created_at}`;
        const response = await axiosInstance.get(`call_center/get/all/users${query}`);
        const { data, meta } = response.data;
        setData(data);
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        });
      } catch {
        message.error(firstApiError(error) || t('callCenter.loadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = debounce((page, status, filters) => {
    fetchCallCenterData(page, status, filters);
  }, 500);

  const handleNameSearch = (e) => {
    const value = e.target.value;
    setSearchName(value);
    debouncedFetch(1, activeStatus, { name: value, phone: searchPhone, created_at: searchDate });
  };

  const handlePhoneSearch = (e) => {
    const value = e.target.value;
    setSearchPhone(value);
    debouncedFetch(1, activeStatus, { name: searchName, phone: value, created_at: searchDate });
  };

  const handleDateSearch = (date) => {
    const formatted = date ? dayjs(date).format("YYYY-MM-DD") : null;
    setSearchDate(formatted);
    debouncedFetch(1, activeStatus, { name: searchName, phone: searchPhone, created_at: formatted });
  };

  const handleTabChange = (key) => {
    setActiveStatus(key);
    fetchCallCenterData(1, key);
  };

  const handleTableChange = (nextPagination) => {
    fetchCallCenterData(nextPagination.current, activeStatus);
  };

  const updateStatus = async (id, newStatus, date = null, time = null) => {
    const currentDate = date && time ? `${date} ${time}` : dayjs().format("YYYY-MM-DD HH:mm:ss");
    try {
      const response = await axiosInstance.put(`call_center/edit/user/${id}`, {
        status: newStatus,
        date: currentDate,
        phone_time: currentDate,
      });

      if (response.status === 200) {
        message.success(t('callCenter.statusUpdated'));
        fetchCallCenterData(pagination.current);
        fetchStatusCounts();
      }
    } catch (error) {
      message.error(t('callCenter.statusError'));
    }
  };

  const handleSetToPlanned = (id) => {
    const record = data.find((item) => item.id === id);
    if (record) {
      setSelectedRecord(record);
      setIsModalVisible(true);
    } else {
      message.error(t('callCenter.notFound'));
    }
  };

  const handleModalOk = () => {
    if (selectedDate && selectedTime && selectedRecord) {
      const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
      const formattedTime = dayjs(selectedTime).format("HH:mm:ss");
      updateStatus(selectedRecord.id, "planned", formattedDate, formattedTime);
      setIsModalVisible(false);
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedRecord(null);
    } else {
      message.error(t('callCenter.pickDateTime'));
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedRecord(null);
  };

  useEffect(() => {
    fetchCallCenterData();
    fetchPatients();
    fetchStatusCounts();
    fetchPartners();
  }, []);

  const columns = [
    {
      title: t('callCenter.searchName'),
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (text, record) => (
        <div className="min-w-0 max-w-[220px]">
          <span className="block truncate text-sm font-medium text-[#1D2C7B]">{highlight(text, searchName)}</span>
          {record.age != null && (
            <span className="mt-0.5 block truncate text-[11px] text-slate-400">
              {record.age}
              {record.is_minor ? ` · ${t('callCenter.minor')}` : ''}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('callCenter.searchPhone'),
      dataIndex: "phone",
      key: "phone",
      width: 132,
      render: (text) => (
        <span className="text-sm tabular-nums text-slate-700 whitespace-nowrap">
          {highlight(text, searchPhone)}
        </span>
      ),
    },
    {
      title: t('callCenter.base'),
      dataIndex: "base",
      key: "base",
      width: 88,
      render: (base) => (
        <StatusBadge tone={base ? "green" : "muted"}>{base ? t('status.inBase') : t('status.notInBase')}</StatusBadge>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: "status",
      key: "status",
      width: 148,
      render: (status, record) => (
        <Select
          size="small"
          value={status}
          onChange={(value) => updateStatus(record.id, value)}
          className="w-full"
        >
          {statusKeys.map((key) => (
            <Option key={key} value={key}>{t(`callCenter.${key}`)}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: t('callCenter.date'),
      key: "when",
      width: 148,
      render: (_, record) => (
        <span className="block text-xs tabular-nums leading-5 text-slate-600 whitespace-nowrap">
          {record.date || t('common.dash')}
          {record.phone_time ? (
            <span className="block text-slate-400">{record.phone_time}</span>
          ) : null}
        </span>
      ),
    },
    {
      title: t('callCenter.source'),
      dataIndex: "partner_id",
      key: "partner_id",
      ellipsis: true,
      width: 120,
      render: (partner_id) => (
        <span className="block truncate text-sm text-slate-600">{partner_id || t('common.dash')}</span>
      ),
    },
    {
      title: t('common.actions'),
      key: "actions",
      width: 84,
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip title={t('callCenter.schedule')}>
            <RowIconButton onClick={() => handleSetToPlanned(record.id)} aria-label={t('callCenter.schedule')}>
              <CalendarDaysIcon className="h-[18px] w-[18px]" />
            </RowIconButton>
          </Tooltip>
          <Tooltip title={t('callCenter.guardians')}>
            <RowIconButton onClick={() => setGuardianLeadId(record.id)} aria-label={t('callCenter.guardians')}>
              <span className="relative inline-flex">
                <UserGroupIcon className="h-[18px] w-[18px]" />
                {record.guardians?.length ? (
                  <span className="absolute -right-1.5 -top-1 min-w-[14px] rounded-full bg-[#1D2C7B] px-0.5 text-center text-[9px] font-bold leading-4 text-white">
                    {record.guardians.length}
                  </span>
                ) : null}
              </span>
            </RowIconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  const renderMobileCard = (record) => (
    <div>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-[#1D2C7B]">
            {highlight(record.name, searchName)}
          </span>
          <a href={`tel:+${record.phone}`} className="mt-0.5 block text-xs tabular-nums text-[#1D2C7B]">
            {highlight(record.phone, searchPhone)}
          </a>
        </div>
        <StatusBadge tone={statusTones[record.status] ?? "muted"}>
          {t(`callCenter.${record.status}`, { defaultValue: record.status })}
        </StatusBadge>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
        <StatusBadge tone={record.base ? "green" : "muted"}>{record.base ? t('callCenter.inBase') : t('status.notInBase')}</StatusBadge>
        {record.age != null && <span>{record.age}{record.is_minor ? ` · ${t('callCenter.minor')}` : ''}</span>}
        {record.date && <span className="tabular-nums">{record.date}</span>}
        {record.phone_time && <span className="tabular-nums">{record.phone_time}</span>}
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <Select
          size="small"
          value={record.status}
          onChange={(value) => updateStatus(record.id, value)}
          className="flex-1 min-w-0"
        >
          {statusKeys.map((key) => (
            <Option key={key} value={key}>{t(`callCenter.${key}`)}</Option>
          ))}
        </Select>
        <Button size="small" type="primary" onClick={() => handleSetToPlanned(record.id)}>
          {t('callCenter.schedule')}
        </Button>
        <Button size="small" onClick={() => setGuardianLeadId(record.id)}>
          {t('callCenter.guardians')}
        </Button>
      </div>
    </div>
  );

  return (
    <PageShell>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        <StatCard tone="blue" count={statusCounts.new} label={t('callCenter.new')} hint={t('callCenter.allTime')} />
        <StatCard tone="amber" count={statusCounts.planned} label={t('callCenter.planned')} hint={t('callCenter.allTime')} />
        <StatCard tone="green" count={statusCounts.completed} label={t('callCenter.completed')} hint={t('callCenter.allTime')} />
        <StatCard tone="red" count={statusCounts.canceled} label={t('callCenter.canceled')} hint={t('callCenter.allTime')} />
      </div>

      <PageCard>
        <PageToolbar
          title={t('callCenter.title')}
          count={pagination.total}
          filters={
            <>
              <Input
                allowClear
                size="small"
                prefix={<MagnifyingGlassIcon className="h-4 w-4 text-slate-400" />}
                placeholder={t('callCenter.searchName')}
                value={searchName}
                onChange={handleNameSearch}
                className="md:!w-[160px]"
              />
              <Input
                allowClear
                size="small"
                placeholder={t('callCenter.searchPhone')}
                value={searchPhone}
                onChange={handlePhoneSearch}
                className="md:!w-[140px]"
              />
              <DatePicker
                size="small"
                placeholder={t('callCenter.date')}
                onChange={handleDateSearch}
                className="md:!w-[132px]"
              />
            </>
          }
          actions={
            <ActionButton icon={<PlusIcon className="h-4 w-4" />} onClick={() => setIsAddModalVisible(true)}>
              {t('callCenter.new')}
            </ActionButton>
          }
        />

        <FilterPills
          value={activeStatus}
          onChange={handleTabChange}
          options={[
            { value: "all", label: t('callCenter.all') },
            { value: "new", label: t('callCenter.new'), count: statusCounts.new },
            { value: "planned", label: t('callCenter.planned'), count: statusCounts.planned },
            { value: "completed", label: t('callCenter.completed'), count: statusCounts.completed },
            { value: "canceled", label: t('callCenter.canceled'), count: statusCounts.canceled },
          ]}
        />

        <DataTable
          rowKey={(record) => record.id ?? record.phone}
          columns={columns}
          dataSource={data}
          loading={loading}
          mobileCard={renderMobileCard}
          pagination={{
            ...pagination,
            showSizeChanger: false,
            position: ["bottomCenter"],
            className: "px-4 pb-3",
            showTotal: (total, range) => (
              <span className="text-xs text-slate-500">
                {t('common.perPage', { from: range[0], to: range[1], total })}
              </span>
            ),
          }}
          onChange={handleTableChange}
          scrollX="max-content"
        />
      </PageCard>

      <Modal
        title={t('callCenter.scheduleCall')}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        centered
        width="100%"
        style={{ maxWidth: 420 }}
      >
        <Space direction="vertical" size="middle" className="w-full">
          <DatePicker
            onChange={(date) => setSelectedDate(date)}
            placeholder={t('callCenter.date')}
            className="w-full"
          />
          <TimePicker
            onChange={(time) => setSelectedTime(time)}
            format="HH:mm"
            placeholder={t('callCenter.time')}
            className="w-full"
          />
        </Space>
      </Modal>

      <Modal
        title={t('callCenter.newUser')}
        open={isAddModalVisible}
        onOk={handleAddUser}
        onCancel={() => setIsAddModalVisible(false)}
        okText={t('callCenter.add')}
        cancelText={t('common.cancel')}
        centered
        width="100%"
        style={{ maxWidth: 480 }}
      >
        <Form layout="vertical">
          <Form.Item label={t('callCenter.searchName')} required>
            <Input
              placeholder={t('callCenter.userName')}
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            />
          </Form.Item>
          <Form.Item label={t('callCenter.source')}>
            <Select
              placeholder={t('callCenter.sourcePlaceholder')}
              value={newUserPartnerId}
              onChange={(value) => setNewUserPartnerId(value)}
              allowClear
            >
              {partners.map((partner) => (
                <Select.Option key={partner.id} value={partner.id}>
                  {partner.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('doctors.phone')} required>
            <Input
              addonBefore="+998"
              maxLength={9}
              placeholder="991234567"
              value={newUser.phone.replace(/^998/, "")}
              onChange={handlePhoneChange}
            />
          </Form.Item>
          <Form.Item label={t('callCenter.birthAt')}>
            <DatePicker
              className="w-full"
              value={newUser.birth_at ? dayjs(newUser.birth_at) : null}
              disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
              onChange={(date) =>
                setNewUser({ ...newUser, birth_at: date ? date.format('YYYY-MM-DD') : null })
              }
            />
          </Form.Item>
          <Divider />
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">{t('callCenter.baseStatus')}</span>
            <StatusBadge tone={newUser.base ? "green" : "muted"}>
              {newUser.base ? t('status.inBase') : t('status.notInBase')}
            </StatusBadge>
          </div>
        </Form>
      </Modal>

      <LeadGuardiansModal
        leadId={guardianLeadId}
        open={Boolean(guardianLeadId)}
        onClose={() => setGuardianLeadId(null)}
        onChanged={() => fetchCallCenterData(pagination.current, activeStatus, {
          name: searchName,
          phone: searchPhone,
          created_at: searchDate,
        })}
      />
    </PageShell>
  );
};

export default CallCenter;
